import React, { useEffect } from 'react';
import { HiOutlineX } from 'react-icons/hi';

/**
 * Reusable modal component.
 * Props: isOpen, onClose, title, size ('sm'|'md'|'lg'|'xl'), children, footer, headerActions,
 *        dismissOnBackdrop (default false — clicking the backdrop does NOT close the modal;
 *        opt in only for read-only or throwaway dialogs where losing the state is harmless)
 */
const Modal = ({ isOpen, open, onClose, title, size = 'md', children, footer, headerActions, dismissOnBackdrop = false }) => {
  const visible = isOpen || open;
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible]);

  if (!visible) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-6xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop — static by default: a stray click must never discard a half-filled form */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={dismissOnBackdrop ? onClose : undefined}
      />
      {/* Modal */}
      <div
        className={`relative bg-white rounded-xl shadow-2xl w-full ${sizeClasses[size]} mx-4 max-h-[90vh] flex flex-col dark:bg-slate-900 dark:border dark:border-slate-800`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-800">
          <h2 className="text-lg font-display font-bold text-navy-800 dark:text-slate-100">{title}</h2>
          <div className="flex items-center gap-3">
            {headerActions}
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors dark:hover:bg-slate-800"
            >
              <HiOutlineX className="w-5 h-5 text-gray-500 dark:text-slate-400" />
            </button>
          </div>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end gap-3 dark:border-slate-800 dark:bg-slate-950/70">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
